import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Spinner } from "reactstrap";

import {
  formatImpersonationRemainingTime,
  IMPERSONATION_BANNER_BODY_CLASS,
} from "../../helpers/impersonation_banner";
import { getImpersonationContext } from "../../helpers/impersonation_storage";
import {
  endImpersonation,
  isImpersonationSession,
} from "../../services/authService";

const ImpersonationBanner = () => {
  const { t } = useTranslation();
  const [ending, setEnding] = useState(false);
  const [remainingTime, setRemainingTime] = useState<string | null>(null);
  const [isExpired, setIsExpired] = useState(false);

  const context = getImpersonationContext();
  const isActive = isImpersonationSession() && context !== null;

  useEffect(() => {
    if (!isActive) {
      document.body.classList.remove(IMPERSONATION_BANNER_BODY_CLASS);
      return;
    }

    document.body.classList.add(IMPERSONATION_BANNER_BODY_CLASS);

    return () => {
      document.body.classList.remove(IMPERSONATION_BANNER_BODY_CLASS);
    };
  }, [isActive]);

  useEffect(() => {
    if (!isActive || !context?.expiresAt) {
      return;
    }

    const updateCountdown = () => {
      const remainingMs =
        new Date(context.expiresAt as string).getTime() - Date.now();

      if (remainingMs <= 0) {
        setIsExpired(true);
        setRemainingTime("00:00");
        return;
      }

      setIsExpired(false);
      setRemainingTime(formatImpersonationRemainingTime(remainingMs));
    };

    updateCountdown();
    const intervalId = window.setInterval(updateCountdown, 1000);

    return () => window.clearInterval(intervalId);
  }, [isActive, context?.expiresAt]);

  if (!isActive || !context) {
    return null;
  }

  const handleEndSession = () => {
    setEnding(true);
    endImpersonation();
    window.location.href = "/super-admin/tenants";
  };

  return (
    <div
      className="impersonation-banner"
      role="alert"
      aria-live="polite"
    >
      <div className="impersonation-banner__inner">
        <div className="impersonation-banner__copy">
          <i
            className="ri-shield-user-line impersonation-banner__icon"
            aria-hidden="true"
          />
          <span className="impersonation-banner__label">
            {t("admin.impersonation.banner.label", {
              tenantName: context.tenantName,
            })}
          </span>
          {context.expiresAt ? (
            <span
              className={
                isExpired
                  ? "impersonation-banner__countdown is-expired"
                  : "impersonation-banner__countdown"
              }
            >
              {isExpired
                ? t("admin.impersonation.banner.expired")
                : t("admin.impersonation.banner.countdown", {
                    time: remainingTime ?? "--:--",
                  })}
            </span>
          ) : null}
        </div>
        <Button
          color="light"
          size="sm"
          className="impersonation-banner__end"
          onClick={handleEndSession}
          disabled={ending}
        >
          {ending ? (
            <Spinner size="sm" className="me-1" />
          ) : (
            <i className="ri-logout-box-r-line align-bottom me-1" />
          )}
          {t("admin.impersonation.banner.endSession")}
        </Button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
